import { isbot } from 'isbot';

import { NonNullish } from 'utils/nullish-check';

import Modal from './modal';

type ContinentCode = 'AF' | 'AS' | 'EU' | 'NA' | 'SA' | 'OC';

type GeojsResponse = {
  region: string;
  latitude: string;
  longitude: string;
  accuracy: number;
  organization_name: string;
  ip: string;
  asn: number;
  organization: string;
  timezone: string;
  country: string;
  area_code: string;
  city: string;
  country_code: string;
  country_code3: string;
  continent_code: ContinentCode;
};

type Shop = {
  url: string;
  icon: string | null;
  redirect: string[];
  code: string;
};

class ShopSwitch {
  public readonly currentShop: Shop | undefined;

  public readonly shops: Shop[];

  private userData: GeojsResponse | undefined;
  private readonly geoTargeting: boolean;

  private readonly container: HTMLElement;
  private icon: HTMLImageElement;
  private select: HTMLSelectElement;
  private submit: HTMLButtonElement;
  private modal;

  constructor(element: string | HTMLElement, geoTargeting: boolean) {
    this.geoTargeting = geoTargeting;
    this.container = NonNullish(
      typeof element === 'string' ? document.querySelector(element) : element,
    );
    this.modal = new Modal(this.container);

    this.select = NonNullish(this.container.querySelector('select'));
    this.icon = NonNullish(this.container.querySelector('img[data-active-icon]'));
    this.submit = NonNullish(this.container.querySelector('button'));

    this.shops = [...this.select.options].map((option) => ({
      url: option.value,
      icon: option.dataset.icon || '',
      redirect: option.dataset.redirect?.split(',') || [],
      code: NonNullish(option.dataset.iso).toUpperCase(),
    }));

    this.currentShop = this.shops.find(
      (shop) => shop.url.includes(window.location.hostname) || shop.url.includes(Shopify.shop),
    );

    this.init();
  }

  init() {
    this.setInitialActive();
    this.addListeners();
    if (this.shouldShow() && this.geoTargeting && !Shopify.designMode) {
      this.getUserLocation().then((response) => {
        this.userData = response as GeojsResponse;
        if (!this.isOnPreferred()) {
          this.open();
        } else {
          localStorage.setItem('geo:dismissed', 'true');
        }
      });
    }
  }

  addListeners() {
    const toggles = document.querySelectorAll(`[data-load="${this.container.dataset.component}"]`);

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        this.setInitialActive();
        this.open();
      });
    });

    this.select.addEventListener('change', () => {
      const selected = NonNullish(
        this.select.querySelector<HTMLOptionElement>(`option[value="${this.select.value}"]`),
      );

      this.icon.src = NonNullish(selected.dataset.icon);
    });

    this.submit.addEventListener('click', () => {
      window.location.replace(this.select.value);
      localStorage.setItem('geo:dismissed', 'true');
    });

    this.modal.on('close', () => {
      if (!localStorage.getItem('geo:dismissed')) localStorage.setItem('geo:dismissed', 'true');
    });
  }

  setInitialActive() {
    [...this.select.options].forEach((option) => {
      if (option.value.includes(location.host) || option.value.includes(Shopify.shop)) {
        this.select.value = option.value;
      }
    });

    this.select.dispatchEvent(new Event('change'));
  }

  public open() {
    this.modal.open();
  }

  public close() {
    this.modal.close();
  }

  // eslint-disable-next-line class-methods-use-this
  private async getUserLocation() {
    const location = await fetch('https://get.geojs.io/v1/ip/geo.json');
    return location.json();
  }

  shouldShow() {
    return (
      window.location.hostname !== 'checkout.shopify.com' &&
      !isbot(navigator.userAgent) &&
      !localStorage.getItem('geo:dismissed') &&
      !window.location.search.includes('?geo') &&
      this.shops.length > 0
    );
  }

  getMatchingStore() {
    const regionalMatch = this.shops.find(
      (shop) =>
        shop.code === this.userData!.country_code ||
        (shop?.redirect?.length && shop?.redirect.includes(this.userData!.country_code)),
    );

    const continentMatch = this.shops.find((shop) => this.userData!.continent_code === shop.code);

    const internationMatch = this.shops.find((shop) => shop.code === 'INT');

    return regionalMatch || continentMatch || internationMatch;
  }

  highlightActive(matchingStore: Shop) {
    const option = this.container.querySelector<HTMLOptionElement>(
      `option[data-iso="${matchingStore.code}"]`,
    );
    if (option) {
      this.select.value = option.value;
      this.select.dispatchEvent(new Event('change'));
    }
  }

  isOnPreferred() {
    const matchingStore = this.getMatchingStore();
    if (matchingStore) this.highlightActive(matchingStore);

    return !!(
      this.currentShop?.code === matchingStore?.code ||
      this.currentShop?.redirect?.includes(this.userData!.country_code)
    );
  }
}

export default (element: string | HTMLElement, geoTargeting: boolean) =>
  new ShopSwitch(element, geoTargeting);
